import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "../../lib/utils";

export type AvatarSize = "xs" | "sm" | "default" | "lg";

const avatarSizeClasses: Record<AvatarSize, string> = {
  xs: "size-4",
  sm: "size-5",
  default: "size-6",
  lg: "size-8",
};

const avatarFallbackTextClasses: Record<AvatarSize, string> = {
  xs: "text-xs",
  sm: "text-xs",
  default: "text-xs",
  lg: "text-xs",
};

const AvatarSizeContext = React.createContext<AvatarSize>("sm");

export interface AvatarProps extends React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Root
> {
  size?: AvatarSize;
}

const Avatar = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, size = "sm", ...props }, ref) => (
  <AvatarSizeContext.Provider value={size}>
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex shrink-0 select-none overflow-hidden rounded-full",
        avatarSizeClasses[size],
        className,
      )}
      {...props}
    />
  </AvatarSizeContext.Provider>
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    // The one sanctioned image edge (borderless contract §1.1): a light photo
    // on a white raised surface has no boundary otherwise. Inset so it does not
    // grow the avatar. Only the image gets it — `AvatarFallback` already has a
    // background of its own.
    className={cn("aspect-square size-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => {
  const size = React.useContext(AvatarSizeContext);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex size-full items-center justify-center rounded-full",
        "bg-hover-strong font-medium text-text-secondary",
        avatarFallbackTextClasses[size],
        className,
      )}
      {...props}
    />
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
